
import React, { type ReactNode, useEffect, useState } from 'react';
import Breadcrumb from './Breadcrumb.tsx';
import Sidebar from './Sidebar.tsx';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore.ts';
import { DynamicBreadcrumbSetter } from './DynamicBreadcrumbSetter.tsx';
import { ModeToggle } from '../ThemeMode/mode-toggle.tsx';
import { PanelLeft } from 'lucide-react';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Separator } from '@core-ui/Components/ui/separator.tsx';
import LanguageToggle from '@ui/shared/LanguageToggle.tsx';
import { useSessionStore } from '@features/auth/store/useSessionStore.ts';
import { useTwilioVoice } from '@hooks/useTwilioVoice.ts';
import { EnvVersionBanner } from '@ui/shared/envVersionBanner.tsx';
import { ENV } from '@constants/env.ts';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { useWizardStore } from '@features/wizard';
import VersionCheckBanner from '@ui/shared/VersionCheckBanner.tsx';
import { useVersionCheck } from '@hooks/useVersionCheck.ts';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { hasNewVersion, newVersion, currentVersion } = useVersionCheck();
  const { items } = useBreadcrumbStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const checkSessionValidity = useSessionStore((state) => state.checkSessionValidity);
  const currentUser = useSessionStore((state) => state.currentUser);
  const identity = currentUser?.userId;
  const loadFeatureFlags = useFeatureFlagsStore((state) => state.loadFeatureFlags);
  const isSessionStarted = useWizardStore((s) => s.isSessionStarted);
  const [isLoaded, setIsLoaded] = useState(false);
  const isCollapsed = !isSidebarOpen;

  useTwilioVoice(identity);

  useEffect(() => {
    if (isSessionStarted) {
      setIsSidebarOpen(false);
    }
  }, [isSessionStarted]);

  useEffect(() => {
    const cleanup = checkSessionValidity();
    return cleanup;
  }, []);

  // Load feature flags when user is authenticated
  useEffect(() => {
    if (currentUser?.organizationId != null) {
      void loadFeatureFlags(currentUser.organizationId);
      setIsLoaded(true);
    }
  }, [currentUser?.organizationId, loadFeatureFlags]);

  useEffect(() => {
    const windowWidth = window.matchMedia('(min-width: 768px)');

    const handleWindowChange = (): void => {
      if (!windowWidth.matches) {
        setIsSidebarOpen(false);
      }
    };

    handleWindowChange();
    windowWidth.addEventListener('change', handleWindowChange);

    return () => {
      windowWidth.removeEventListener('change', handleWindowChange);
    };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <Sidebar isOpen={!isCollapsed} />

      <main data-qa-id="sdr-admin-main" className="flex flex-1 flex-col overflow-hidden bg-background text-foreground">

        {/* Version Banner */}
        {isLoaded &&
            <>
              {hasNewVersion && (
                <VersionCheckBanner
                  oldVersion={currentVersion}
                  newVersion={newVersion}/>
              )}
            </>
        }

        {/* Header - fixed height */}
        <div className="flex shrink-0 items-center justify-between pb-4 px-3 md:px-4 lg:px-6 pt-3 md:pt-4 lg:pt-6 min-h-[36px]">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                setIsSidebarOpen(prev => !prev);
              }}
              variant="ghost"
              size="icon"
              aria-label="Toggle sidebar"
              className="hidden md:flex h-8 w-8 transition-colors hover:bg-muted dark:hover:bg-mute"
            >
              <PanelLeft size={18} className="text-muted-foreground" />
            </Button>

            <Separator
              orientation="vertical"
              className="hidden md:flex mr-2 data-[orientation=vertical]:h-4"
            />

            <div className="flex items-center space-x-2">
              <DynamicBreadcrumbSetter />
              <Breadcrumb items={items} />
            </div>
          </div>
          {ENV !== 'prod' && (
            <>
              <LanguageToggle />
              <div className="flex items-center gap-2">
                <EnvVersionBanner/>
                <ModeToggle/>
              </div>
            </>
          )}
        </div>

        {/* Content area - scrollable */}
        <div className="min-h-0 flex-1 overflow-y-auto p-3 md:p-4 lg:p-6">
          {children}
        </div>

      </main>
    </div>
  );
};

export default MainLayout;
